import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { DialogHelper } from "@core/helpers/dialog-helper";
import { AdvisorInvoiceDetails } from "@core/models/advisor-invoice-details.model";
import { FinancialInstitute } from "@core/models/financial-insitute.model";
import {
  Intermediary,
  TIntermediariesPaginated,
} from "@core/models/intermediary.model";
import { AdvisorInvoiceDetailsService } from "@core/services/advisor-invoice-details.service";
import { ManagementService } from "@core/services/management.service";
import { NotificationService } from "@core/services/notification.service";
import { Utilities } from "@core/utils/utils";
import { TableSorting, TPagination } from "@custom-types/sorting";
import { ToasterSeverities } from "@enums/toaster-severities";
import {
  AccountManager,
  TAccountManagersPaginated,
} from "@models/account-manager.model";
import { Advisor, TAdvisorsPaginated } from "@models/advisor.model";
import { Country } from "@models/country.model";
import { Document, TDocumentsPaginated } from "@models/document.model";
import { Email, TEmailsPaginated } from "@models/email.model";
import { Etf } from "@models/etf-model";
import { Insurer, TInsurersPaginated } from "@models/insurer.model";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from "rxjs";

import * as managementActions from "./management.actions";
import { SET_MANAGEMENT_LOADING_STATUS } from "./management.actions";
import {
  SELECT_DOCUMENTS_PAGINATION,
  SELECT_MANAGEMENT_PAGINATION,
} from "./management.selectors";

@Injectable()
export class ManagementEffects {
  loadAdvisors$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_ADVISORS),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .getAdvisors$(action.searchTerm, action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response: Array<Advisor> | TAdvisorsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      managementActions.SET_MANAGEMENT_LIST_LENGTH({
                        length: response.length,
                      }),
                      managementActions.SET_MANAGEMENT_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_ADVISORS({
                        advisorList: data,
                      })
                    );
                  }
                  return of(
                    managementActions.SET_ADVISORS({ advisorList: response })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_ADVISORS({ advisorList: [] }),
                  managementActions.SET_MANAGEMENT_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_MANAGEMENT_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_MANAGEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadInsurers$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_INSURERS),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .getInsurers$(action.searchTerm, action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response: Array<Insurer> | TInsurersPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      managementActions.SET_MANAGEMENT_LIST_LENGTH({
                        length: response.length,
                      }),
                      managementActions.SET_MANAGEMENT_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_INSURERS({
                        insurerList: data,
                      })
                    );
                  }
                  return of(
                    managementActions.SET_INSURERS({ insurerList: response })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_INSURERS({ insurerList: [] }),
                  managementActions.SET_MANAGEMENT_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_MANAGEMENT_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_MANAGEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadAccountManagers$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_ACCOUNT_MANAGERS),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .getAccountManagers$(
              action.searchTerm,
              action.pagination ?? pagination
            )
            .pipe(
              switchMap(
                (
                  response: Array<AccountManager> | TAccountManagersPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                  );
                  if ("data" in response && "totalRecords" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      managementActions.SET_MANAGEMENT_LIST_LENGTH({
                        length: response.totalRecords,
                      }),
                      managementActions.SET_MANAGEMENT_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_ACCOUNT_MANAGERS({
                        accountManagerList: data,
                      })
                    );
                  }
                  return of(
                    managementActions.SET_ACCOUNT_MANAGERS({
                      accountManagerList: response,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_ACCOUNT_MANAGERS({
                    accountManagerList: [],
                  }),
                  managementActions.SET_MANAGEMENT_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_MANAGEMENT_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_MANAGEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadFinancialInstitutions$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_FINANCIAL_INSTITUTIONS),
      mergeMap((action: { searchTerm?: string }): Observable<Action> => {
        this.store.dispatch(
          managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
            loading: true,
          })
        );
        return this.managementService
          .getFinancialInstitutions$(action.searchTerm)
          .pipe(
            switchMap(
              (
                financialInstituteList: Array<FinancialInstitute>
              ): Observable<Action> => {
                this.store.dispatch(
                  managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
                    loading: false,
                  })
                );
                return of(
                  managementActions.SET_FINANCIAL_INSTITUTIONS({
                    financialInstituteList,
                  })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of(
                managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
                  loading: false,
                }),
                managementActions.SET_FINANCIAL_INSTITUTIONS({
                  financialInstituteList: [],
                })
              );
            })
          );
      })
    );
  });

  loadIntermediaries$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_INTERMEDIARIES),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .getIntermediaries$(
              action.searchTerm,
              action.pagination ?? pagination
            )
            .pipe(
              switchMap(
                (
                  response: Array<Intermediary> | TIntermediariesPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      managementActions.SET_MANAGEMENT_LIST_LENGTH({
                        length: response.length,
                      }),
                      managementActions.SET_MANAGEMENT_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_INTERMEDIARIES({
                        intermediaryList: data,
                      })
                    );
                  }
                  return of(
                    managementActions.SET_INTERMEDIARIES({
                      intermediaryList: response,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_INTERMEDIARIES({
                    intermediaryList: [],
                  }),
                  managementActions.SET_MANAGEMENT_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_MANAGEMENT_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_MANAGEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadActiveIntermediaries$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_ACTIVE_INTERMEDIARIES),
      mergeMap((action: { searchTerm?: string }): Observable<Action> => {
        return this.managementService
          .getActiveIntermediaries$(action.searchTerm)
          .pipe(
            map((response: Array<Intermediary>): Action => {
              return managementActions.SET_ACTIVE_INTERMEDIARIES({
                intermediaryList: response,
              });
            }),
            catchError((): Observable<Action> => {
              return of(
                managementActions.SET_ACTIVE_INTERMEDIARIES({
                  intermediaryList: [],
                })
              );
            })
          );
      })
    );
  });

  /**
   * Saves the advisor and (TL-1119) its invoice details. An existing advisor's
   * details go first, so a refusal (shown with the backend's message) leaves
   * the dialog open with nothing half-saved. A new advisor is created first;
   * its details then need the returned id, and when they are refused the
   * advisor still exists, so the dialog closes and the message says what to
   * fill in.
   */
  saveAdvisors$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_ADVISOR),
      mergeMap(
        (action: {
          advisor: Partial<Advisor>;
          id: string | undefined;
          invoiceDetails?: AdvisorInvoiceDetails | null;
          saveAdvisor?: boolean;
        }): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          const create = !action.id;
          const details = action.invoiceDetails ?? null;
          let detailsRefused = false;
          const saveDetailsFirst$: Observable<unknown> =
            !create && details
              ? this.advisorInvoiceDetailsService.saveDetails$(
                  action.id!,
                  details
                )
              : of(null);
          return saveDetailsFirst$.pipe(
            switchMap(
              (): Observable<unknown> =>
                create || action.saveAdvisor !== false
                  ? this.managementService.saveAdvisor$(
                      action.advisor,
                      action.id
                    )
                  : of(null)
            ),
            switchMap(
              (createdId: unknown): Observable<unknown> =>
                create && details && typeof createdId === "string"
                  ? this.advisorInvoiceDetailsService
                      .saveDetails$(createdId, details)
                      .pipe(
                        catchError(
                          (response: HttpErrorResponse): Observable<null> => {
                            this.notificationService.show(
                              ToasterSeverities.Error,
                              "ADVISOR_INVOICE_DETAILS_NOT_SAVED",
                              response
                            );
                            detailsRefused = true;
                            return of(null);
                          }
                        )
                      )
                  : of(null)
            ),
            switchMap((): Observable<Action> => {
              this.dialog.closeAll();
              this.store.dispatch(
                SET_MANAGEMENT_LOADING_STATUS({ loading: false })
              );
              // A refused new advisor's details already toasted why; a success
              // toast would replace that message.
              if (!detailsRefused) {
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "ADVISOR_SAVED"
                );
              }
              return of(managementActions.LOAD_ADVISORS({}));
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "ADVISOR_SAVE_ERROR",
                response
              );
              this.store.dispatch(
                SET_MANAGEMENT_LOADING_STATUS({ loading: false })
              );
              return of();
            })
          );
        }
      )
    );
  });

  saveInsurer$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_INSURER),
      mergeMap(
        (action: {
          insurer: Partial<Insurer>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .saveInsurer$(action.insurer, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "INSURER_SAVED",
                  undefined,
                  { name: action.insurer.name ?? "" }
                );
                return of(managementActions.LOAD_INSURERS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "INSURER_SAVE_ERROR",
                  response,
                  { name: action.insurer.name ?? "" }
                );
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                return of();
              })
            );
        }
      )
    );
  });

  loadEmails$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_EMAILS),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          { pagination?: Partial<TPagination> },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            managementActions.SET_EMAILS_LOADING_STATUS({ loading: true })
          );
          return this.managementService
            .getEmails$(action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response: Array<Email> | TEmailsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    managementActions.SET_EMAILS_LOADING_STATUS({
                      loading: false,
                    })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      managementActions.SET_MANAGEMENT_LIST_LENGTH({
                        length: response.length,
                      }),
                      managementActions.SET_MANAGEMENT_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_EMAILS({
                        emailList: data,
                      })
                    );
                  }
                  return of(
                    managementActions.SET_EMAILS({ emailList: response })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_EMAILS({ emailList: [] }),
                  managementActions.SET_MANAGEMENT_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_MANAGEMENT_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_EMAILS_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadDocuments$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_DOCUMENTS),
      withLatestFrom(this.store.select(SELECT_DOCUMENTS_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          { pagination?: Partial<TPagination> },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            managementActions.SET_DOCUMENTS_LOADING_STATUS({ loading: true })
          );
          return this.managementService
            .getDocuments$(action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response: Array<Document> | TDocumentsPaginated
                ): Observable<Action> => {
                  let lastActionDate = new Date();
                  this.store.dispatch(
                    managementActions.SET_DOCUMENTS_LOADING_STATUS({
                      loading: false,
                    })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    lastActionDate = new Date(
                      Math.max(
                        ...data.map((document: Document): number =>
                          new Date(document.utcUpdateDate).getTime()
                        )
                      )
                    );
                    return of(
                      managementActions.SET_DOCUMENTS_LIST_LENGTH({
                        length: response.length,
                      }),
                      managementActions.SET_DOCUMENTS_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_DOCUMENTS({
                        documentList: response.data,
                      }),
                      managementActions.SET_LAST_ACTION_DATE({
                        date: lastActionDate,
                      })
                    );
                  }
                  lastActionDate = new Date(
                    Math.max(
                      ...response.map((document: Document): number =>
                        new Date(document.utcUpdateDate).getTime()
                      )
                    )
                  );
                  return of(
                    managementActions.SET_DOCUMENTS({ documentList: response }),
                    managementActions.SET_LAST_ACTION_DATE({
                      date: lastActionDate,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_DOCUMENTS({ documentList: [] }),
                  managementActions.SET_DOCUMENTS_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_DOCUMENTS_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_DOCUMENTS_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadCountries$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_COUNTRIES),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
        return this.managementService.getCountries$().pipe(
          switchMap((countries: Array<Country>): Observable<Action> => {
            this.store.dispatch(
              SET_MANAGEMENT_LOADING_STATUS({ loading: false })
            );
            return of(
              managementActions.SET_COUNTRIES({ countryList: countries })
            );
          }),
          catchError((): Observable<Action> => {
            return of(
              SET_MANAGEMENT_LOADING_STATUS({ loading: false }),
              managementActions.SET_COUNTRIES({ countryList: [] })
            );
          })
        );
      })
    );
  });

  saveFinancialInstitution$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_FINANCIAL_INSTITUTION),
      mergeMap(
        (action: {
          institution: Partial<FinancialInstitute>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(
            managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
              loading: true,
            })
          );
          return this.managementService
            .saveFinancialInstitution$(action.institution, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeTopDialog();
                this.store.dispatch(
                  managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
                    loading: false,
                  })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "FINANCIAL_INSTITUTE_SAVED",
                  undefined,
                  { name: action.institution.name ?? "" }
                );
                return of(managementActions.LOAD_FINANCIAL_INSTITUTIONS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "FINANCIAL_INSTITUTE_SAVE_ERROR",
                  response,
                  { name: action.institution.name ?? "" }
                );
                return of(
                  managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  saveAccountManager$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_ACCOUNT_MANAGER),
      mergeMap(
        (action: {
          accountManager: Partial<AccountManager>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .saveAccountManager$(action.accountManager, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "ACCOUNT_MANAGER_SAVED"
                );
                return of(managementActions.LOAD_ACCOUNT_MANAGERS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "ACCOUNT_MANAGER_SAVE_ERROR",
                  response
                );
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                return of();
              })
            );
        }
      )
    );
  });

  saveIntermediary$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_INTERMEDIARY),
      mergeMap(
        (action: {
          intermediary: Partial<Intermediary>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .saveIntermediary$(action.intermediary, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "INTERMEDIARY_SAVED"
                );
                return of(managementActions.LOAD_INTERMEDIARIES({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "INTERMEDIARY_SAVE_ERROR",
                  response
                );
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                return of();
              })
            );
        }
      )
    );
  });

  uploadDocument$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.UPLOAD_DOCUMENT),
      mergeMap((action: { id: string; document: File }): Observable<Action> => {
        return this.managementService
          .uploadDocument$(action.id, action.document)
          .pipe(
            switchMap((): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Success,
                "DOCUMENT_UPLOADED"
              );
              return of(
                managementActions.SET_LAST_ACTION_DATE({ date: new Date() })
              );
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "DOCUMENT_UPLOAD_ERROR",
                response
              );
              return of();
            })
          );
      })
    );
  });

  downloadDocument$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.DOWNLOAD_DOCUMENT),
      mergeMap(
        (action: { id: string; documentName: string }): Observable<Action> => {
          return this.managementService.getDocument$(action.id).pipe(
            switchMap((file: Blob): Observable<Action> => {
              Utilities.downloadAttachment(file, action.documentName);
              this.notificationService.show(
                ToasterSeverities.Success,
                "DOCUMENT_DOWNLOADED"
              );
              return of();
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "DOCUMENT_DOWNLOAD_ERROR",
                response
              );
              return of();
            })
          );
        }
      )
    );
  });

  loadEmail$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_EMAIL),
      concatMap((action: { id: string }): Observable<Action> => {
        return this.managementService.getEmail$(action.id).pipe(
          switchMap((email: Email): Observable<Action> => {
            return of(managementActions.SET_EMAIL({ email }));
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "EMAIL_TEMPLATE_LOAD_ERROR",
              response
            );
            return of();
          })
        );
      })
    );
  });

  saveEmail$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_EMAIL),
      mergeMap((action: { email: Partial<Email> }): Observable<Action> => {
        this.store.dispatch(
          managementActions.SET_EMAILS_LOADING_STATUS({ loading: true })
        );
        return this.managementService.saveEmail$(action.email).pipe(
          switchMap((): Observable<Action> => {
            this.dialog.closeAll();
            this.store.dispatch(
              managementActions.SET_EMAILS_LOADING_STATUS({ loading: false })
            );
            this.notificationService.show(
              ToasterSeverities.Success,
              "EMAIL_TEMPLATE_SAVED"
            );
            return of(managementActions.LOAD_EMAILS({}));
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "EMAIL_TEMPLATE_SAVE_ERROR",
              response
            );
            this.store.dispatch(
              managementActions.SET_EMAILS_LOADING_STATUS({ loading: false })
            );
            return of();
          })
        );
      })
    );
  });

  loadETFList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_ETF_LIST),

      switchMap(
        (action: {
          searchTerm?: string;
          sorting?: TableSorting;
        }): Observable<Action> => {
          const searchTerm = action.searchTerm?.trim() || undefined;

          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));

          return this.managementService.getETFList$(searchTerm).pipe(
            switchMap((etfList: Array<Etf>): Observable<Action> => {
              return of(
                SET_MANAGEMENT_LOADING_STATUS({ loading: false }),

                managementActions.SET_MANAGEMENT_PAGINATION({
                  pagination: searchTerm ? { searchTerm } : undefined,
                }),

                managementActions.SET_ETF_LIST({ etfList })
              );
            }),

            catchError((): Observable<Action> => {
              return of(
                SET_MANAGEMENT_LOADING_STATUS({ loading: false }),

                managementActions.SET_MANAGEMENT_PAGINATION({
                  pagination: undefined,
                }),

                managementActions.SET_ETF_LIST({
                  etfList: [],
                })
              );
            })
          );
        }
      )
    );
  });

  saveETF$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_ETF),
      mergeMap(
        (action: {
          etf: Partial<Etf>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService.saveETF$(action.etf).pipe(
            switchMap((): Observable<Action> => {
              this.dialog.closeAll();
              this.store.dispatch(
                SET_MANAGEMENT_LOADING_STATUS({ loading: false })
              );
              this.notificationService.show(
                ToasterSeverities.Success,
                "FUND_SAVED",
                undefined,
                { name: action.etf.name ?? "" }
              );
              return of(managementActions.LOAD_ETF_LIST({}));
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "FUND_SAVE_ERROR",
                response,
                { name: action.etf.name ?? "" }
              );
              this.store.dispatch(
                SET_MANAGEMENT_LOADING_STATUS({ loading: false })
              );
              return of();
            })
          );
        }
      )
    );
  });

  deleteETF$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.DELETE_ETF),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.managementService.deleteETF$(action.id).pipe(
          concatMap((): Observable<Action> => {
            return of(managementActions.LOAD_ETF_LIST({}));
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  checkFundISIN$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.CHECK_FUND_ISIN),
      mergeMap((action: { isin: string }): Observable<Action> => {
        return this.managementService.checkFundIsin$(action.isin).pipe(
          switchMap((valid: boolean): Observable<Action> => {
            return of(managementActions.SET_FUND_ISIN_VALIDITY({ valid }));
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  checkFundAbbreviation$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.CHECK_FUND_ABBREVIATION),
      mergeMap((action: { abbreviation: string }): Observable<Action> => {
        return this.managementService
          .checkFundAbbreviation$(action.abbreviation)
          .pipe(
            switchMap((valid: boolean): Observable<Action> => {
              return of(
                managementActions.SET_FUND_ABBREVIATION_VALIDITY({ valid })
              );
            }),
            catchError((): Observable<Action> => {
              return of();
            })
          );
      })
    );
  });

  checkForExistingEmail$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.CHECK_FOR_EXISTING_EMAIL),
      mergeMap((action: { email: string }): Observable<Action> => {
        return this.managementService.checkForExistingEmail$(action.email).pipe(
          switchMap((emailExists: boolean): Observable<Action> => {
            return of(
              managementActions.SET_EMAIL_VALIDITY({
                emailExists: emailExists,
              })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  checkForExistingUsername$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.CHECK_FOR_EXISTING_USERNAME),
      mergeMap((action: { username: string }): Observable<Action> => {
        return this.managementService
          .checkForExistingUsername$(action.username)
          .pipe(
            switchMap((usernameExists: boolean): Observable<Action> => {
              return of(
                managementActions.SET_USERNAME_VALIDITY({
                  usernameExists: usernameExists,
                })
              );
            }),
            catchError((): Observable<Action> => {
              return of();
            })
          );
      })
    );
  });

  validateIban$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.VALIDATE_IBAN),
      mergeMap((action: { iban: string }): Observable<Action> => {
        return this.managementService.validateIban$(action.iban).pipe(
          switchMap(
            (
              financialInstituteList: Array<FinancialInstitute>
            ): Observable<Action> => {
              if (!Array.isArray(financialInstituteList)) {
                return of(
                  managementActions.SET_FINANCIAL_INSTITUTIONS({
                    financialInstituteList: [],
                  })
                );
              }
              return of(
                managementActions.SET_FINANCIAL_INSTITUTIONS({
                  financialInstituteList,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of(
              managementActions.SET_FINANCIAL_INSTITUTIONS({
                financialInstituteList: [],
              })
            );
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private managementService: ManagementService,
    private notificationService: NotificationService,
    private store: Store,
    private dialog: DialogHelper,
    private advisorInvoiceDetailsService: AdvisorInvoiceDetailsService
  ) {}
}
